import { Navigate, useParams } from 'react-router-dom';
import { PageLoader } from '../shared/ui/PageLoader.js';
import { useAuth } from '../features/auth/index.js';
import { resolveActiveId } from './activeBusiness.js';
import { useMyBusinesses } from './BusinessesContext.js';

/**
 * Sends a pre-multi-business URL (`/`, `/calls`, `/settings`, `/onboarding`, …)
 * to the same section under the resolved active business (doc 07 §8.1). Keeps
 * old bookmarks and in-app links working after the routing change.
 */
export function LegacyRedirect({ section }: { section: string }) {
  const { businesses, loading } = useMyBusinesses();
  const { user } = useAuth();

  if (loading) return <PageLoader />;
  if (businesses.length === 0) return <Navigate to="/businesses/new" replace />;

  const id = resolveActiveId(businesses, user?.uid ?? '');
  if (!id) return <Navigate to="/businesses/new" replace />;
  const suffix = section ? `/${section}` : '';
  return <Navigate to={`/b/${id}${suffix}`} replace />;
}

/**
 * `/b/:id/number` → Settings › Number. The number's standalone page became a
 * Settings section; this keeps bookmarks and in-flight links working. Built from
 * the param rather than a relative `..` so the target doesn't depend on how the
 * route happens to be nested.
 */
export function NumberRedirect() {
  const { businessId } = useParams<{ businessId: string }>();
  return <Navigate to={`/b/${businessId}/settings?section=number`} replace />;
}

/**
 * `/u/:businessId` → the plan picker (doc 26 D8).
 *
 * The target of the short link in the minute-allowance warning SMS. It is a
 * separate route rather than a query on the destination because the whole point
 * is length — every character saved here is a character the warning message can
 * spend on saying what is about to happen.
 *
 * It lands on **Settings › Billing** (doc 42), not on the plan picker. The
 * prepaid low-balance SMS uses the same link to say "top up here". Everyone who
 * receives that SMS is by definition already subscribed — the warning is about
 * a plan's allowance. Settings shows the counter that prompted the message
 * and carries both routes onward: the Stripe portal for a plan change, and the
 * plans (or the activation widget) for anyone who has lapsed.
 */
export function UpgradeRedirect() {
  const { businessId } = useParams<{ businessId: string }>();
  return <Navigate to={`/b/${businessId}/settings?section=billing`} replace />;
}
